// Material formula parser and evaluator.
//
// This is a line-for-line port of the two Jace 1.0.0 classes the desktop
// estimating app runs every material formula through
// (BidlightApp.ViewModel/Main/Estimation/Materials/MaterialFormula.cs):
//
//   new TokenReader(CultureInfo.InvariantCulture).Read(formula)
//   new AstBuilder(new FunctionRegistry(false), false).Build(tokens)
//
// A formula is valid on the web exactly when it is valid on the desktop,
// and it fails with the same message. Quirks are kept on purpose:
//
// * The function registry is EMPTY (the desktop never registers sin, max,
//   etc.), so "sin(x)" is a variable followed by brackets and is rejected.
// * Variables are case-insensitive: they are lower-cased in the tree.
// * "2x" is not implicit multiplication; it is rejected with
//   'Unexpected integer constant "2" found.'
// * A number the reader cannot parse ("1.2.3") is dropped without an error,
//   exactly as Jace drops it.
//
// The desktop only lists the variables it finds under + - * / ^ and
// functions (MaterialFormula.GetVariables); a variable that sits only under
// unary minus, % or a comparison is never offered for mapping, so it
// evaluates to 0 there. `desktopVariables` reproduces that list, and
// `variables` is the full list, so the editor can warn about the gap.

export class FormulaParseError extends Error {
  constructor(message) {
    super(message);
    this.name = "FormulaParseError";
  }
}

const DECIMAL_SEPARATOR = ".";
const ARGUMENT_SEPARATOR = ",";

const T_INTEGER = "Integer";
const T_FLOAT = "FloatingPoint";
const T_TEXT = "Text";
const T_OPERATION = "Operation";
const T_LEFT = "LeftBracket";
const T_RIGHT = "RightBracket";
const T_ARGSEP = "ArgumentSeparator";

const isDigit = (c) => c >= "0" && c <= "9";

function isPartOfNumeric(c, isFirstCharacter, isFormulaSubPart) {
  if (
    c !== DECIMAL_SEPARATOR &&
    !isDigit(c) &&
    (!(isFormulaSubPart && isFirstCharacter) || c !== "-") &&
    (isFirstCharacter || c !== "e")
  ) {
    if (!isFirstCharacter) return c === "E";
    return false;
  }
  return true;
}

function isPartOfVariable(c, isFirstCharacter) {
  if (
    (c < "a" || c > "z") &&
    (c < "A" || c > "Z") &&
    (isFirstCharacter || !isDigit(c))
  ) {
    if (!isFirstCharacter) return c === "_";
    return false;
  }
  return true;
}

const isScientificNotation = (c) => c === "e" || c === "E";

// .NET int.TryParse(NumberStyles.Integer): optional surrounding white space,
// optional sign, digits, and a value that fits in an Int32.
function tryParseInt(s) {
  if (!/^\s*[+-]?\d+\s*$/.test(s)) return null;
  const v = Number(s.trim());
  if (v > 2147483647 || v < -2147483648) return null;
  return v;
}

// .NET double.TryParse(NumberStyles.Float | AllowThousands, invariant).
function tryParseDouble(s) {
  if (!/^\s*[+-]?(\d[\d,]*(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(s)) {
    return null;
  }
  const v = Number(s.trim().replace(/,/g, ""));
  return Number.isFinite(v) ? v : null;
}

function isUnaryMinus(c, tokens) {
  if (c !== "-") return false;
  const last = tokens[tokens.length - 1];
  if (!last) {
    throw new FormulaParseError("Index was out of range.");
  }
  if (
    last.type !== T_FLOAT &&
    last.type !== T_INTEGER &&
    last.type !== T_TEXT
  ) {
    return last.type !== T_RIGHT;
  }
  return false;
}

export function tokenize(formula) {
  if (formula === null || formula === undefined || formula === "") {
    throw new FormulaParseError("Formula can't be empty.");
  }
  const tokens = [];
  const a = String(formula).split("");
  let isFormulaSubPart = true;
  let flag = false;
  const invalid = (i) =>
    new FormulaParseError(`Invalid token "${a[i]}" detected at position ${i}.`);

  for (let i = 0; i < a.length; i++) {
    if (isPartOfNumeric(a[i], true, isFormulaSubPart)) {
      let sb = a[i];
      const start = i;
      while (++i < a.length && isPartOfNumeric(a[i], false, isFormulaSubPart)) {
        if (flag && isScientificNotation(a[i])) throw invalid(i);
        if (isScientificNotation(a[i])) {
          flag = true;
          if (i + 1 >= a.length) {
            throw new FormulaParseError(
              "Index was outside the bounds of the array."
            );
          }
          if (a[i + 1] === "-") {
            sb += a[i++];
          }
        }
        sb += a[i];
      }
      const asInt = tryParseInt(sb);
      if (asInt !== null) {
        tokens.push({ type: T_INTEGER, value: asInt, start, length: i - start });
        isFormulaSubPart = false;
      } else {
        const asDouble = tryParseDouble(sb);
        if (asDouble !== null) {
          tokens.push({ type: T_FLOAT, value: asDouble, start, length: i - start });
          flag = false;
          isFormulaSubPart = false;
        } else if (sb === "-") {
          tokens.push({ type: T_OPERATION, value: "_", start, length: 1 });
        }
      }
      if (i === a.length) continue;
    }

    if (isPartOfVariable(a[i], true)) {
      let text = a[i];
      const start = i;
      while (++i < a.length && isPartOfVariable(a[i], false)) {
        text += a[i];
      }
      tokens.push({ type: T_TEXT, value: text, start, length: i - start });
      isFormulaSubPart = false;
      if (i === a.length) continue;
    }

    if (a[i] === ARGUMENT_SEPARATOR) {
      tokens.push({ type: T_ARGSEP, value: a[i], start: i, length: 1 });
      isFormulaSubPart = false;
      continue;
    }

    const twoChar = (second, value) => {
      if (i + 1 < a.length && a[i + 1] === second) {
        tokens.push({ type: T_OPERATION, value, start: i, length: 2 });
        i++;
        return true;
      }
      return false;
    };

    switch (a[i]) {
      case "%":
      case "*":
      case "+":
      case "-":
      case "/":
      case "^":
      case "≠":
      case "≤":
      case "≥":
        tokens.push({
          type: T_OPERATION,
          value: isUnaryMinus(a[i], tokens) ? "_" : a[i],
          start: i,
          length: 1,
        });
        isFormulaSubPart = true;
        break;
      case "(":
        tokens.push({ type: T_LEFT, value: "(", start: i, length: 1 });
        isFormulaSubPart = true;
        break;
      case ")":
        tokens.push({ type: T_RIGHT, value: ")", start: i, length: 1 });
        isFormulaSubPart = false;
        break;
      case "<":
        if (!twoChar("=", "≤")) {
          tokens.push({ type: T_OPERATION, value: "<", start: i, length: 1 });
        }
        isFormulaSubPart = false;
        break;
      case ">":
        if (!twoChar("=", "≥")) {
          tokens.push({ type: T_OPERATION, value: ">", start: i, length: 1 });
        }
        isFormulaSubPart = false;
        break;
      case "!":
        if (twoChar("=", "≠")) {
          isFormulaSubPart = false;
          break;
        }
        throw invalid(i);
      case "&":
        if (twoChar("&", "&")) {
          isFormulaSubPart = false;
          break;
        }
        throw invalid(i);
      case "|":
        if (twoChar("|", "|")) {
          isFormulaSubPart = false;
          break;
        }
        throw invalid(i);
      case "=":
        if (twoChar("=", "=")) {
          isFormulaSubPart = false;
          break;
        }
        throw invalid(i);
      case " ":
        break;
      default:
        throw invalid(i);
    }
  }
  return tokens;
}

const PRECEDENCE = {
  "(": 0,
  "&": 1,
  "|": 1,
  "<": 2,
  ">": 2,
  "≤": 2,
  "≥": 2,
  "≠": 2,
  "=": 2,
  "+": 3,
  "-": 3,
  "*": 4,
  "/": 4,
  "%": 4,
  _: 6,
  "^": 5,
};

const isLeftAssociative = (c) => c === "*" || c === "+" || c === "-" || c === "/";

const BINARY = new Set(["+", "-", "*", "/", "%", "^", "&", "|", "<", "≤", ">", "≥", "=", "≠"]);

export function buildAst(tokens) {
  const resultStack = [];
  const operatorStack = [];

  const pop = (token) => {
    if (!resultStack.length) {
      throw new FormulaParseError(
        `There is a syntax issue for the operation "${token.value}" at position ${token.start}. The number of arguments does not match with what is expected.`
      );
    }
    return resultStack.pop();
  };

  const convertOperation = (token) => {
    if (token.value === "_") {
      const arg = pop(token);
      return { kind: "unary", op: "_", args: [arg] };
    }
    if (BINARY.has(token.value)) {
      const right = pop(token);
      const left = pop(token);
      return { kind: "binary", op: token.value, args: [left, right] };
    }
    throw new FormulaParseError(`Unknown operation "${token.value}".`);
  };

  const popOperations = (untilLeftBracket, current) => {
    while (
      operatorStack.length &&
      operatorStack[operatorStack.length - 1].type !== T_LEFT
    ) {
      const t = operatorStack.pop();
      if (t.type === T_OPERATION) resultStack.push(convertOperation(t));
    }
    if (untilLeftBracket) {
      if (
        !operatorStack.length ||
        operatorStack[operatorStack.length - 1].type !== T_LEFT
      ) {
        throw new FormulaParseError(
          `No matching left bracket found for the right bracket at position ${current.start}.`
        );
      }
      operatorStack.pop();
    } else if (
      operatorStack.length &&
      operatorStack[operatorStack.length - 1].type === T_LEFT &&
      (!current || current.type !== T_ARGSEP)
    ) {
      throw new FormulaParseError(
        `No matching right bracket found for the left bracket at position ${
          operatorStack[operatorStack.length - 1].start
        }.`
      );
    }
  };

  for (const token of tokens) {
    switch (token.type) {
      case T_INTEGER:
        resultStack.push({ kind: "int", value: token.value });
        break;
      case T_FLOAT:
        resultStack.push({ kind: "float", value: token.value });
        break;
      case T_TEXT:
        // No functions and no constants are registered on the desktop, so
        // every name is a variable.
        resultStack.push({ kind: "var", name: String(token.value).toLowerCase() });
        break;
      case T_LEFT:
        operatorStack.push(token);
        break;
      case T_RIGHT:
        popOperations(true, token);
        break;
      case T_ARGSEP:
        popOperations(false, token);
        // Jace counts arguments on a stack only a function call opens; with
        // no functions there is none, and Jace throws "Stack empty".
        throw new FormulaParseError(
          `Unexpected argument separator "," at position ${token.start}.`
        );
      case T_OPERATION: {
        const c = token.value;
        while (
          operatorStack.length &&
          operatorStack[operatorStack.length - 1].type === T_OPERATION
        ) {
          const top = operatorStack[operatorStack.length - 1];
          const key = top.value;
          if (
            (!isLeftAssociative(c) || PRECEDENCE[c] > PRECEDENCE[key]) &&
            PRECEDENCE[c] >= PRECEDENCE[key]
          ) {
            break;
          }
          operatorStack.pop();
          resultStack.push(convertOperation(top));
        }
        operatorStack.push(token);
        break;
      }
      default:
        break;
    }
  }

  popOperations(false, null);

  if (resultStack.length > 1) {
    // Stack.ToArray() is top first; Jace inspects every entry but the top.
    const arr = [...resultStack].reverse();
    for (let i = 1; i < arr.length; i++) {
      if (arr[i].kind === "int") {
        throw new FormulaParseError(`Unexpected integer constant "${arr[i].value}" found.`);
      }
      if (arr[i].kind === "float") {
        throw new FormulaParseError(
          `Unexpected floating point constant "${arr[i].value}" found.`
        );
      }
    }
    throw new FormulaParseError("The syntax of the provided formula is not valid.");
  }
  if (!resultStack.length) {
    throw new FormulaParseError("The syntax of the provided formula is not valid.");
  }
  return resultStack[resultStack.length - 1];
}

export function parseFormula(formula) {
  return buildAst(tokenize(formula));
}

const dependsOnVariables = (node) =>
  node.kind === "var" || (node.args || []).some(dependsOnVariables);

function collectAll(node, out) {
  if (node.kind === "var") {
    if (!out.includes(node.name)) out.push(node.name);
    return;
  }
  (node.args || []).forEach((a) => collectAll(a, out));
}

// MaterialFormula.GetVariables: only + * - / ^ (and functions, of which
// there are none) are walked.
const DESKTOP_WALKED = new Set(["+", "*", "-", "/", "^"]);
function collectDesktop(node, out) {
  if (!dependsOnVariables(node)) return;
  if (node.kind === "var") {
    if (!out.includes(node.name)) out.push(node.name);
    return;
  }
  if (node.kind === "binary" && DESKTOP_WALKED.has(node.op)) {
    node.args.forEach((a) => collectDesktop(a, out));
  }
}

// One call for the editor: { valid, error, variables, desktopVariables }.
export function validateFormula(formula) {
  try {
    const ast = parseFormula(formula);
    const variables = [];
    const desktopVariables = [];
    collectAll(ast, variables);
    collectDesktop(ast, desktopVariables);
    return { valid: true, error: null, ast, variables, desktopVariables };
  } catch (e) {
    return {
      valid: false,
      error: e && e.message ? e.message : "The syntax of the provided formula is not valid.",
      ast: null,
      variables: [],
      desktopVariables: [],
    };
  }
}

function execute(node, vars) {
  switch (node.kind) {
    case "int":
    case "float":
      return node.value;
    case "var":
      if (Object.prototype.hasOwnProperty.call(vars, node.name)) {
        return vars[node.name];
      }
      throw new FormulaParseError(`The variable "${node.name}" used is not defined.`);
    case "unary":
      return 0 - execute(node.args[0], vars);
    case "binary": {
      const l = execute(node.args[0], vars);
      const r = execute(node.args[1], vars);
      switch (node.op) {
        case "+":
          return l + r;
        case "-":
          return l - r;
        case "*":
          return l * r;
        case "/":
          return l / r;
        case "%":
          return l % r;
        case "^":
          return Math.pow(l, r);
        case "&":
          return l !== 0 && r !== 0 ? 1 : 0;
        case "|":
          return l !== 0 || r !== 0 ? 1 : 0;
        case "<":
          return l < r ? 1 : 0;
        case "≤":
          return l <= r ? 1 : 0;
        case ">":
          return l > r ? 1 : 0;
        case "≥":
          return l >= r ? 1 : 0;
        case "=":
          return l === r ? 1 : 0;
        case "≠":
          return l !== r ? 1 : 0;
        default:
          throw new FormulaParseError(`Unknown operation "${node.op}".`);
      }
    }
    default:
      throw new FormulaParseError("The syntax of the provided formula is not valid.");
  }
}

// Interpreter.Execute with case-insensitive variables. Throws on a missing
// variable; the desktop's Calculate() turns any failure into 0, which
// `calculateLikeDesktop` reproduces.
export function evaluateFormula(formula, variables = {}) {
  const ast = typeof formula === "string" ? parseFormula(formula) : formula;
  const vars = {};
  Object.keys(variables || {}).forEach((k) => {
    vars[k.toLowerCase()] = Number(variables[k]);
  });
  return execute(ast, vars);
}

export function calculateLikeDesktop(formula, variables = {}) {
  try {
    return evaluateFormula(formula, variables);
  } catch (e) {
    return 0;
  }
}

// BidlightApp.ViewModel/Enums/MaterialFormulaParameter.cs, in enum order:
// the desktop serialises the parameter by its index in formula_maps.
export const MATERIAL_FORMULA_PARAMETERS = [
  { value: 0, key: "ComponentLength", label: "Component length" },
  { value: 1, key: "ComponentWidth", label: "Component width" },
  { value: 2, key: "ComponentDepth", label: "Component depth" },
  { value: 3, key: "ComponentWeight", label: "Component weight" },
  { value: 4, key: "ComponentNominalWeight", label: "Component nominal weight" },
  { value: 5, key: "ComponentArea", label: "Component area" },
  { value: 6, key: "ComponentVolume", label: "Component volume" },
  { value: 7, key: "ComponentQuantity", label: "Component quantity" },
  { value: 8, key: "MaterialThickness", label: "Material thickness" },
];

// formula_maps.FormulaConfig is the desktop MaterialFormula serialised with
// Newtonsoft defaults: { FormulaName, Formula, VariableParamMap: [{ Variable,
// Parameter }] } with Parameter as the enum index (or a name, if someone
// wrote it with a string converter). Returns null for anything else.
export function parseFormulaConfig(json) {
  if (!json) return null;
  try {
    const cfg = typeof json === "string" ? JSON.parse(json) : json;
    if (!cfg || typeof cfg !== "object") return null;
    const map = (cfg.VariableParamMap || []).map((m) => {
      let p = m ? m.Parameter : null;
      if (typeof p === "string") {
        const hit = MATERIAL_FORMULA_PARAMETERS.find((x) => x.key === p);
        p = hit ? hit.value : null;
      }
      return { Variable: m ? m.Variable : "", Parameter: p === undefined ? null : p };
    });
    return { FormulaName: cfg.FormulaName || "", Formula: cfg.Formula || "", VariableParamMap: map };
  } catch (e) {
    return null;
  }
}

export function serializeFormulaConfig(cfg) {
  return JSON.stringify({
    FormulaName: cfg.FormulaName || "",
    Formula: cfg.Formula || "",
    VariableParamMap: (cfg.VariableParamMap || []).map((m) => ({
      Variable: m.Variable,
      Parameter: m.Parameter === "" || m.Parameter === undefined ? null : m.Parameter,
    })),
  });
}
